import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Offcanvas } from 'bootstrap';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import { UserInfo } from '../../models/users.model';
import {
  RECURRENCE_FREQUENCY_OPTIONS,
  Recurrence,
  TASK_PRIORITY_OPTIONS,
  TASK_TYPE_OPTIONS,
  toDateInput,
} from '../task.model';
import { LazyPicker, propertyPicker, staffPicker } from '../lazy-picker';

@Component({
  selector: 'app-task-recurrences',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    BreadcrumbComponent,
    PaginationComponent,
  ],
  templateUrl: './task-recurrences.component.html',
  styleUrls: ['./task-recurrences.component.css'],
})
export class TaskRecurrencesComponent implements OnInit, AfterViewInit {
  @ViewChild('recurrenceModal') modalElement!: ElementRef;
  private modal: Offcanvas | null = null;

  breadcrumbList = {
    breadcrumb_path: 'Tasks',
    currentURL: 'Recurring tasks',
    breadcrumb_url: '/admin/tasks',
  };

  recurrences: Recurrence[] = [];
  total = 0;
  page = 1;
  limit = 50;
  totalPage = 1;

  form!: FormGroup;
  showModal = false;
  isEditMode = false;
  selectedId = '';
  checklist: string[] = [];
  newChecklistLabel = '';

  typeOptions = TASK_TYPE_OPTIONS;
  priorityOptions = TASK_PRIORITY_OPTIONS;
  frequencyOptions = RECURRENCE_FREQUENCY_OPTIONS;
  users: LazyPicker;
  properties: LazyPicker;

  canManage = false;

  constructor(
    private fb: FormBuilder,
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private alertService: NgxToastrService,
    private destroyRef: DestroyRef,
  ) {
    this.users = staffPicker(httpService, destroyRef);
    this.properties = propertyPicker(httpService, destroyRef);
    this.initForm();
  }

  ngOnInit(): void {
    this.canManage = this.localStorageService.checkPermission(USER_PERMISSIONS.TASKS.TASKS_MANAGE_TEMPLATES);
    this.load();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.modalElement?.nativeElement;
      if (el) this.modal = Offcanvas.getOrCreateInstance(el, { backdrop: 'static', keyboard: false });
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      task_type: [null, Validators.required],
      priority: ['Medium'],
      property_id: [null],
      assignee_id: [null, Validators.required],
      frequency: ['Weekly', Validators.required],
      interval: [1, [Validators.required, Validators.min(1)]],
      start_date: [toDateInput(new Date()), Validators.required],
      end_date: [''],
      due_after_days: [0, [Validators.min(0)]],
      planned_start: [''],
      planned_end: [''],
      is_active: [true],
    });
  }

  load(keepPage = false): void {
    if (!keepPage) this.page = 1;
    this.loadingService.show();
    this.httpService.getAllTaskRecurrences(this.page, this.limit).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          const d = response.data;
          this.recurrences = Array.isArray(d) ? d : d?.recurrences ?? d?.rows ?? [];
          this.total = Array.isArray(d) ? d.length : d?.total ?? this.recurrences.length;
          this.totalPage = Array.isArray(d) ? 1 : d?.pageCount ?? 1;
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  pageChange(newPage: number): void {
    if (this.page === newPage) return;
    this.page = newPage;
    this.load(true);
  }

  frequencyLabel(r: Recurrence): string {
    return r.interval && r.interval > 1 ? `Every ${r.interval} × ${r.frequency.toLowerCase()}` : r.frequency;
  }

  // ── Create / edit ──────────────────────────────────────────────────────
  openModal(r?: Recurrence): void {
    this.form.reset({
      priority: 'Medium',
      frequency: 'Weekly',
      interval: 1,
      start_date: toDateInput(new Date()),
      due_after_days: 0,
      is_active: true,
    });
    this.checklist = [];
    this.newChecklistLabel = '';
    this.users.init();
    this.properties.init();
    const me = this.localStorageService.getItem<UserInfo>('userInfo');
    if (me?.id) this.users.ensure({ value: me.id, label: `${me.first_name ?? ''} ${me.last_name ?? ''}`.trim() });

    if (r) {
      this.isEditMode = true;
      this.selectedId = r.id;
      if (r.assignee) this.users.ensure({ value: r.assignee.id, label: r.assignee.name });
      if (r.property) this.properties.ensure({ value: r.property.id, label: r.property.name });
      this.form.patchValue({
        title: r.title,
        description: r.description ?? '',
        task_type: r.task_type,
        priority: r.priority ?? 'Medium',
        property_id: r.property?.id ?? null,
        assignee_id: r.assignee?.id ?? null,
        frequency: r.frequency,
        interval: r.interval ?? 1,
        start_date: r.start_date ?? '',
        end_date: r.end_date ?? '',
        due_after_days: r.due_after_days ?? 0,
        planned_start: r.planned_start ?? '',
        planned_end: r.planned_end ?? '',
        is_active: r.is_active,
      });
      this.checklist = [...(r.checklist ?? [])];
    } else {
      this.isEditMode = false;
      this.selectedId = '';
      this.form.patchValue({ assignee_id: me?.id ?? null });
    }
    this.showModal = true;
    this.modal?.show();
  }

  closeModal(): void {
    this.showModal = false;
    this.modal?.hide();
  }

  addChecklistItem(): void {
    const label = this.newChecklistLabel.trim();
    if (!label) return;
    this.checklist.push(label);
    this.newChecklistLabel = '';
  }

  removeChecklistItem(i: number): void {
    this.checklist.splice(i, 1);
  }

  trackByIndex(i: number): number {
    return i;
  }

  isFieldInvalid(name: string): boolean {
    const c = this.form.get(name);
    return !!(c && c.invalid && c.touched);
  }

  submit(): void {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((c) => c.markAsTouched());
      return;
    }
    const v = this.form.value;
    const body: Record<string, any> = {
      title: v.title,
      description: v.description || '',
      task_type: v.task_type,
      priority: v.priority || 'Medium',
      property_id: v.property_id || null,
      assignee_id: v.assignee_id,
      checklist: this.checklist.map((c) => c.trim()).filter(Boolean),
      frequency: v.frequency,
      interval: Number(v.interval) || 1,
      start_date: v.start_date,
      end_date: v.end_date || null,
      due_after_days: Number(v.due_after_days) || 0,
      planned_start: v.planned_start || null,
      planned_end: v.planned_end || null,
      is_active: !!v.is_active,
    };
    this.loadingService.show();
    const call = this.isEditMode
      ? this.httpService.updateTaskRecurrence(this.selectedId, body)
      : this.httpService.createTaskRecurrence(body);
    call.subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message, 'toast-top-right');
          this.closeModal();
          this.load(true);
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  toggleActive(r: Recurrence): void {
    const next = !r.is_active;
    r.is_active = next;
    this.httpService.updateTaskRecurrence(r.id, { is_active: next }).subscribe({
      next: (response: any) => {
        if (response.success && response.data?.id) Object.assign(r, response.data);
      },
      error: () => (r.is_active = !next),
    });
  }

  delete(r: Recurrence): void {
    if (!confirm(`Delete the recurring template "${r.title}"? Tasks already created are kept.`)) return;
    this.loadingService.show();
    this.httpService.deleteTaskRecurrence(r.id).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.alertService.success(response.message, 'toast-top-right');
          this.load(true);
        }
      },
      error: () => this.loadingService.hide(),
    });
  }
}
